import { Component } from '@theme/component';

/**
 * Multiple images with text.
 * Sincroniza a lista de textos (um visível por vez) com a pilha/coleção de imagens.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} items - Blocos de texto (um por item com conteúdo).
 * @property {HTMLElement[]} [images] - Wrappers das imagens.
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class MultipleImagesWithText extends Component {
  requiredRefs = ['items'];

  #index = 0;

  /** Último índice de imagem que esteve em destaque (usado quando o texto atual não tem imagem). */
  #imageIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#update();
  }

  /**
   * Seleciona um item pelo índice (número) ou pelo id do bloco.
   * Usado via on:click="/select/{{ block.id }}".
   * @param {number|string} input
   * @param {Event} [event]
   */
  select(input, event) {
    const { items } = this.refs;
    let index = typeof input === 'number' ? input : items.findIndex((item) => item.dataset.blockId === input);

    if (index < 0 || index >= items.length || index === this.#index) return;

    event?.preventDefault();
    this.#index = index;
    this.#update();
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault();
    const { items } = this.refs;
    this.#index = (this.#index + 1) % items.length;
    this.#update();
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault();
    const { items } = this.refs;
    this.#index = (this.#index - 1 + items.length) % items.length;
    this.#update();
  }

  #update() {
    const { items, images = [] } = this.refs;
    const current = items[this.#index];
    if (!current) return;

    items.forEach((item, i) => {
      const selected = i === this.#index;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-hidden', String(!selected));
      item.toggleAttribute('inert', !selected);
    });

    if (images.length === 0) return;

    const matched = images.findIndex((image) => image.dataset.blockId === current.dataset.blockId);
    if (matched >= 0) this.#imageIndex = matched;

    images.forEach((image, i) => {
      // Posição na pilha: 0 = topo. Usada pelo layout "stacked".
      const position = (i - this.#imageIndex + images.length) % images.length;
      image.style.setProperty('--stack-position', String(position));
      image.toggleAttribute('data-selected', position === 0);
    });
  }
}

if (!customElements.get('multiple-images-with-text-component')) {
  customElements.define('multiple-images-with-text-component', MultipleImagesWithText);
}
